import React, { useState, useEffect,useRef } from "react"
import {
  Card, Typography, Space, Tag, Button, Input, Tabs, Row, Col,
  message, Pagination, Empty, Spin, Dropdown, Menu, Tooltip,
  Select, Badge, Layout, Modal, Form, Table
} from "antd"
import {
  SearchOutlined, DeleteOutlined, EditOutlined, BookOutlined,
  CalendarOutlined, SortAscendingOutlined, FilterOutlined,
  HistoryOutlined, BookFilled, EnvironmentOutlined, AppstoreOutlined,
  BarsOutlined
} from "@ant-design/icons"
import { useSearchHistory } from "../hooks/useSearchHistory"
import moment from 'moment'
import cssStyles from './Monster.module.css'

import LocationSelect from './LocationSelect';
import { getLocationById, getLocationSuggestions, getDisplayNameFromId } from '../utils/findLocation';

const SearchControls = ({
  searchTerm,
  onSearchChange,
  sortBy,
  onSortChange,
  dateFilter,
  onDateFilterChange,
  viewMode,
  onViewModeChange
}) => (
  <div className={cssStyles.searchControls}>
    <Row gutter={[16, 16]} align="middle">
      <Col flex="auto">
        <Input
          placeholder="Search history..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={e => {
            // Ensure we're passing the value, not the event
            onSearchChange(e.target.value);
          }}
          className={cssStyles.searchInput}
        />
      </Col>
      <Col>
        <Select
          placeholder="Sort by"
          value={sortBy}
          onChange={onSortChange}
          style={{ width: 140 }}
          suffixIcon={<SortAscendingOutlined />}
        >
          <Option value="date">Date</Option>
          <Option value="matches">Matches</Option>
          <Option value="lastUsed">Last Used</Option>
        </Select>
      </Col>
      <Col>
        <Select
          placeholder="Date range"
          value={dateFilter}
          onChange={onDateFilterChange}
          style={{ width: 140 }}
          suffixIcon={<CalendarOutlined />}
        >
          <Option value="today">Today</Option>
          <Option value="week">This Week</Option>
          <Option value="month">This Month</Option>
          <Option value="all">All Time</Option>
        </Select>
      </Col>
      <Col>
        <Button.Group>
          <Tooltip title="Card View">
            <Button 
              type={viewMode === 'card' ? 'primary' : 'default'}
              icon={<AppstoreOutlined />}
              onClick={() => onViewModeChange('card')}
            />
          </Tooltip>
          <Tooltip title="Table View">
            <Button 
              type={viewMode === 'table' ? 'primary' : 'default'}
              icon={<BarsOutlined />}
              onClick={() => onViewModeChange('table')}
            />
          </Tooltip>
        </Button.Group>
      </Col>
    </Row>
  </div>
);

const EmptyState = ({ type, isError, error }) => (
  <Empty
    image={isError ? Empty.PRESENTED_IMAGE_SIMPLE : Empty.PRESENTED_IMAGE_DEFAULT}
    description={
      isError
        ? `Error loading ${type} searches: ${error?.message || 'Unknown error'}`
        : type === 'recent'
          ? "No recent searches yet"
          : type === 'bookmarked'
            ? "No bookmarked searches yet"
            : "No archived searches"
    }
  />
);

// Define a helper function outside of any component to safely handle search objects
const safelyHandleSearch = (search, onSearch) => {
  try {
    if (!search) {
      console.warn('Empty search object provided');
      message.error('Please enter at least a keyword or job title to search');
      return;
    }
    
    // If search is a string, use it directly as a keyword
    if (typeof search === 'string') {
      if (!search.trim()) {
        message.error('Please enter at least a keyword or job title to search');
        return;
      }
      onSearch({ keyword: search, radius: "30" });
      return;
    }
    
    // If search is an object with a query property
    if (search.query) {
      // If query is a string, use it as keyword
      if (typeof search.query === 'string') {
        if (!search.query.trim()) {
          message.error('Please enter at least a keyword or job title to search');
          return;
        }
        onSearch({ keyword: search.query, radius: "30" });
        return;
      }
      
      // If query is an object, use it directly
      if (typeof search.query === 'object') {
        // Make a copy to avoid reference issues
        const searchParams = { ...search.query };
        
        // Ensure we have at least a keyword or job title
        if (!searchParams.keyword && !searchParams.jobTitle && !searchParams.targetJobTitle) {
          if (search.title) {
            searchParams.keyword = search.title;
          } else {
            message.error('Please enter at least a keyword or job title to search');
            return;
          }
        }
        
        // Set default radius if not specified
        if (!searchParams.radius) {
          searchParams.radius = "30";
        }
        
        onSearch(searchParams);
        return;
      }
    }
    
    // If search has a title property, use that as a keyword
    if (search.title) {
      onSearch({ keyword: search.title, radius: "30" });
      return;
    }
    
    // Fallback to an empty search with a warning
    console.warn('Could not extract search parameters from:', search);
    message.error('Please enter at least a keyword or job title to search');
  } catch (error) {
    console.error('Error handling search:', error);
    // Fallback to an empty search
    message.error('An error occurred while processing your search');
  }
};

// Helper function to safely format dates
const formatDate = (dateString) => {
  if (!dateString) return 'Unknown date';
  
  // Try to parse the date in various formats
  let date;
  if (typeof dateString === 'string') {
    // Check if it's in MM/DD/YYYY format
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      const [month, day, year] = dateString.split('/');
      date = moment(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    } else {
      // Try standard ISO format
      date = moment(dateString);
    }
  } else {
    // If it's already a Date object or timestamp
    date = moment(dateString);
  }
  
  // Check if the date is valid
  if (!date.isValid()) {
    console.warn(`Invalid date format: ${dateString}`);
    return 'Invalid date';
  }
  
  return date;
};

const { Text, Title } = Typography
const { TabPane } = Tabs
const { Option } = Select
const { Content } = Layout;

const SearchHistory = ({ onSearch }) => {
  const {
    recentSearches,
    bookmarkedSearches,
    archivedSearches,
    addRecentSearch,
    bookmarkSearch,
    deleteSearch,
    toggleArchive,
    updateSearch,
    refreshSearches,
    loading,
    error
  } = useSearchHistory();

  const [searchTerm, setSearchTerm] = useState("")
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const [activeTab, setActiveTab] = useState('recent');
  const [sortBy, setSortBy] = useState('date');
  const [dateFilter, setDateFilter] = useState('all');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentSearch, setCurrentSearch] = useState(null);
  const [viewMode, setViewMode] = useState('card');
  const [form] = Form.useForm();
  const searchTimeout = useRef(null);


  useEffect(() => {
    if (recentSearches?.pagination) {
      setPagination({
        current: recentSearches.pagination.page,
        pageSize: recentSearches.pagination.limit,
        total: recentSearches.pagination.total
      });
    }
  }, [recentSearches.pagination]);

  const handleEdit = (search) => {
    setCurrentSearch(search);
    
    // Get location value for LocationSelect component
    const location = search.query?.location;
    let locationValue = '';
    
    if (location) {
      if (typeof location === 'object') {
        // If it's an object, preserve the structure for LocationSelect
        locationValue = location;
      } else if (typeof location === 'string') {
        // If it's a string, check if it's a numeric ID
        if (/^\d+$/.test(location)) {
          // It's a numeric ID, convert to object with name
          const displayName = getDisplayNameFromId(location);
          locationValue = {
            locationId: location,
            name: displayName
          };
        } else {
          // It's a regular string (name), pass as is
          locationValue = location;
        }
      } else if (typeof location === 'number') {
        // If it's a number, convert to string ID and get display name
        const locationId = String(location);
        const displayName = getDisplayNameFromId(locationId);
        locationValue = {
          locationId: locationId,
          name: displayName
        };
      }
    }
    
    form.setFieldsValue({
      title: search.title || '',
      location: locationValue,
      jobType: search.query?.jobType || '',
      experience: search.query?.experience || '',
      keyword: search.query?.keyword || search.query?.jobTitle || search.query?.targetJobTitle || '',
      minSalary: search.query?.salary?.min || '',
      maxSalary: search.query?.salary?.max || '',
      radius: search.query?.radius || '30'
    });
    setEditModalVisible(true);
  }
const handleSearchChange = (value) => {
  setSearchTerm(value);
  

  setPagination(prev => ({
    ...prev,
    current: 1
  }));
  

  if (searchTimeout.current) {
    clearTimeout(searchTimeout.current);
  }
  
  searchTimeout.current = setTimeout(() => {
    refreshSearches(1, pagination.pageSize, value);
  }, 300); 
};
  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      try {
        // Use the updateSearch function from the hook
        await updateSearch(currentSearch.id, values);
        
        message.success('Search updated successfully');
        setEditModalVisible(false);
        
        // The updateSearch function will refresh the searches internally
      } catch (error) {
        console.error('API Error:', error);
        message.error('Failed to update search on the server');
      }
    } catch (validationError) {
      console.error('Validation error:', validationError);
      message.error('Please check the form fields and try again');
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteSearch(id);
      message.success('Search deleted successfully');
    } catch (error) {
      message.error('Failed to delete search');
    }
  }

  const filterSearches = (searches) => {
    if (!searches || !Array.isArray(searches)) return [];
    return searches.filter((search) =>
      search?.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  const handleBookmarkSearch = async (search) => {
    try {
      await bookmarkSearch(search);
      message.success('Search bookmarked successfully');
    } catch (error) {
      message.error('Failed to bookmark search');
    }
  };

  const handleArchiveSearch = async (searchId) => {
    try {
      await toggleArchive(searchId);
      message.success('Search archive status updated successfully');
    } catch (error) {
      message.error('Failed to update archive status');
    }
  };

  const handlePageChange = (page, pageSize) => {
    refreshSearches(page, pageSize, searchTerm);
  };

  const SearchCard = React.memo(({ search, onEdit, onDelete, onBookmark, onArchive }) => {
    // Enhanced function to get proper location display name with ID lookup
    const getLocationDisplayName = () => {
      const location = search.query?.location;
      if (!location) return null;
      
      if (typeof location === 'object') {
        // Handle different location object structures
        if (location.name) {
          return location.name;
        } else if (location.locationName) {
          return location.locationName;
        } else if (location.locationId || location.id) {
          // Use our location lookup utility to get a human-readable name
          return getDisplayNameFromId(location.locationId || location.id);
        }
      } else if (typeof location === 'string') {
        // Check if it's a numeric string (likely a location ID)
        if (/^\d+$/.test(location)) {
          return getDisplayNameFromId(location);
        }
        // Otherwise it's already a display name
        return location;
      }
      
      // Fallback to whatever we have
      return String(location);
    };

    return (
      <Card
        className={cssStyles.searchHistoryCard}
        hoverable
        onClick={() => safelyHandleSearch(search, onSearch)}
      >
        <div className={cssStyles.searchCardHeader}>
          <div className={cssStyles.searchCardTitle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 8 }}>
              <Title level={5} style={{
                margin: 0,
                fontSize: '16px', // Reduce font size
                color: '#1a1a1a',
                fontWeight: 600,
                maxWidth: '200px', // Limit width
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              title={search.title || "Untitled Search"} // Add tooltip on hover
              >
                {search.title || "Untitled Search"}
              </Title>
              <Badge
                count={search.matchedCandidateCount}
                style={{
                  backgroundColor: "#52c41a",
                  marginLeft: '8px',
                  flexShrink: 0
                }}
                title="Matched candidates"
              />
            </div>
            <Space size="small" style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                <CalendarOutlined style={{ marginRight: 4 }} />
                Last used {search.lastUsed ? formatDate(search.lastUsed).fromNow() : 'Never'}
              </Text>
            </Space>
          </div>
          <Space
            className={cssStyles.searchCardActions}
          >
            <Tooltip title={search.isBookmarked ? "Remove bookmark" : "Bookmark search"}>
              <Button
                type={search.isBookmarked ? "primary" : "default"}
                icon={search.isBookmarked ? <BookFilled /> : <BookOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onBookmark(search);
                }}
                size="middle"
                style={{
                  borderRadius: '8px',
                  transition: 'all 0.3s ease'
                }}
              />
            </Tooltip>
            <Tooltip title="Edit search">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(search);
                }}
                size="middle"
                style={{
                  borderRadius: '8px',
                  color: '#595959'
                }}
              />
            </Tooltip>
            <Tooltip title={search.status === 'archived' ? "Unarchive search" : "Archive search"}>
              <Button
                type="text"
                icon={<HistoryOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onArchive(search.id);
                }}
                size="middle"
                style={{
                  borderRadius: '8px',
                  color: search.status === 'archived' ? '#1890ff' : '#595959'
                }}
              />
            </Tooltip>
            <Tooltip title="Delete search">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(search.id);
                }}
                size="middle"
                style={{
                  borderRadius: '8px'
                }}
              />
            </Tooltip>
          </Space>
        </div>

        <div className={cssStyles.searchCardContent}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Space wrap style={{ marginBottom: 12 }}>
              {getLocationDisplayName() && (
                <Tag color="blue" style={{ padding: '4px 12px', borderRadius: '6px' }}>
                  <EnvironmentOutlined style={{ marginRight: 4 }} />
                  {getLocationDisplayName()}
                </Tag>
              )}
              {search.query?.jobType && (
                <Tag color="green" style={{ padding: '4px 12px', borderRadius: '6px' }}>
                  <span style={{ marginRight: 4 }}>💼</span>
                  {search.query.jobType}
                </Tag>
              )}
              {search.query?.experience && (
                <Tag color="orange" style={{ padding: '4px 12px', borderRadius: '6px' }}>
                  <span style={{ marginRight: 4 }}>⭐</span>
                  {search.query.experience}
                </Tag>
              )}
              {search.query?.salary?.min && search.query?.salary?.max && (
                <Tag color="purple" style={{ padding: '4px 12px', borderRadius: '6px' }}>
                  <span style={{ marginRight: 4 }}>💰</span>
                  ${search.query.salary.min}k - ${search.query.salary.max}k
                </Tag>
              )}
            </Space>
            <Text type="secondary" style={{ fontSize: '12px', color: '#8c8c8c' }}>
              <HistoryOutlined style={{ marginRight: 4 }} />
              Created {search.createdAt ? formatDate(search.createdAt).format("MMM D, YYYY") : 'Unknown'}
            </Text>
          </Space>
        </div>
      </Card>
    );
  });

  // Add a table view component
  const SearchTable = React.memo(({ searches, onEdit, onDelete, onBookmark, onArchive }) => {
    // Enhanced function to get proper location display name with ID lookup
    const getLocationDisplayName = (search) => {
      const location = search.query?.location;
      if (!location) return null;
      
      if (typeof location === 'object') {
        // Handle different location object structures
        if (location.name) {
          return location.name;
        } else if (location.locationName) {
          return location.locationName;
        } else if (location.locationId || location.id) {
          // Use our location lookup utility to get a human-readable name
          return getDisplayNameFromId(location.locationId || location.id);
        }
      } else if (typeof location === 'string') {
        // Check if it's a numeric string (likely a location ID)
        if (/^\d+$/.test(location)) {
          return getDisplayNameFromId(location);
        }
        // Otherwise it's already a display name
        return location;
      }
      
      // Fallback to whatever we have
      return String(location);
    };

    const columns = [
      {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
        ellipsis: true,
        render: (text, record) => (
          <div style={{ cursor: 'pointer' }} onClick={() => safelyHandleSearch(record, onSearch)}>
            <Tooltip title={text || "Untitled Search"}>
              <span>{text || "Untitled Search"}</span>
            </Tooltip>
            {record.matchedCandidateCount > 0 && (
              <Badge
                count={record.matchedCandidateCount}
                style={{
                  backgroundColor: "#52c41a",
                  marginLeft: '8px'
                }}
                title="Matched candidates"
              />
            )}
          </div>
        ),
        sorter: (a, b) => (a.title || "").localeCompare(b.title || "")
      },
      {
        title: 'Location',
        key: 'location',
        render: (_, record) => {
          const location = getLocationDisplayName(record);
          return location ? (
            <Tag color="blue">
              <EnvironmentOutlined style={{ marginRight: 4 }} />
              {location}
            </Tag>
          ) : null;
        },
        width: 180
      },
      {
        title: 'Job Type',
        key: 'jobType',
        render: (_, record) => record.query?.jobType ? (
          <Tag color="green">
            <span style={{ marginRight: 4 }}>💼</span>
            {record.query.jobType}
          </Tag>
        ) : null,
        width: 150
      },
      {
        title: 'Experience',
        key: 'experience',
        render: (_, record) => record.query?.experience ? (
          <Tag color="orange">
            <span style={{ marginRight: 4 }}>⭐</span>
            {record.query.experience}
          </Tag>
        ) : null,
        width: 150
      },
      {
        title: 'Last Used',
        key: 'lastUsed',
        render: (_, record) => (
          <span>
            {record.lastUsed ? formatDate(record.lastUsed).fromNow() : 'Never'}
          </span>
        ),
        sorter: (a, b) => {
          if (!a.lastUsed && !b.lastUsed) return 0;
          if (!a.lastUsed) return 1;
          if (!b.lastUsed) return -1;
          return new Date(b.lastUsed) - new Date(a.lastUsed);
        },
        width: 150
      },
      {
        title: 'Created',
        key: 'createdAt',
        render: (_, record) => (
          <span>
            {record.createdAt ? formatDate(record.createdAt).format("MMM D, YYYY") : 'Unknown'}
          </span>
        ),
        sorter: (a, b) => {
          if (!a.createdAt && !b.createdAt) return 0;
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          return new Date(b.createdAt) - new Date(a.createdAt);
        },
        width: 120
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Space>
            <Tooltip title={record.isBookmarked ? "Remove bookmark" : "Bookmark search"}>
              <Button
                type={record.isBookmarked ? "primary" : "default"}
                icon={record.isBookmarked ? <BookFilled /> : <BookOutlined />}
                onClick={() => onBookmark(record)}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Edit search">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
                size="small"
              />
            </Tooltip>
            <Tooltip title={record.status === 'archived' ? "Unarchive search" : "Archive search"}>
              <Button
                type="text"
                icon={<HistoryOutlined />}
                onClick={() => onArchive(record.id)}
                size="small"
                style={{
                  color: record.status === 'archived' ? '#1890ff' : '#595959'
                }}
              />
            </Tooltip>
            <Tooltip title="Delete search">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(record.id)}
                size="small"
              />
            </Tooltip>
          </Space>
        ),
        width: 180,
        fixed: 'right'
      }
    ];

    return (
      <Table
        dataSource={searches}
        columns={columns}
        rowKey="id"
        size="middle"
        pagination={false} // Keep this false as we're using a custom pagination component
        scroll={{ x: 1100 }}
      />
    );
  });

  const renderSearches = (searches, type) => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      );
    }

    if (error) {
      return <EmptyState type={type} isError error={error} />;
    }
      const displayedSearches = (type !== 'recent' && searchTerm) 
    ? filterSearches(searches) 
    : searches;

  if (!displayedSearches || displayedSearches.length === 0) {
    return <EmptyState type={type} />;
  }


    return (
      <>
        {viewMode === 'table' ? (
          <SearchTable
            searches={displayedSearches}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onBookmark={handleBookmarkSearch}
            onArchive={handleArchiveSearch}
          />
        ) : (
          <div className={cssStyles.searchHistoryCardGrid}>
            {displayedSearches.map((search) => (
              <SearchCard
                key={search.id}
                search={search}
                onEdit={() => handleEdit(search)}
                onDelete={() => handleDelete(search.id)}
                onBookmark={() => handleBookmarkSearch(search)}
                onArchive={() => handleArchiveSearch(search.id)}
              />
            ))}
          </div>
        )}
        
        {displayedSearches.length > 0 && !loading && !error && type === 'recent' && (
          <div style={{ 
            marginTop: '16px', 
            textAlign: 'center', 
            paddingBottom: '16px',
            backgroundColor: 'white',
            position: 'sticky',
            bottom: 0,
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.06)'
          }}>
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={handlePageChange}
              showSizeChanger
              showQuickJumper
              showTotal={(total) => `Total ${total} items`}
              style={{ padding: '12px 0' }}
            />
          </div>
        )}
      </>
    );
  };

  // Create tab items for the Tabs component
  const tabItems = [
    {
      key: 'recent',
      label: (
        <Badge count={recentSearches?.pagination?.total || 0}>
          Recent Searches
        </Badge>
      ),
      children: (
        <div className={cssStyles.searchHistoryTabpaneContent}>
          {renderSearches(recentSearches.data, 'recent')}
        </div>
      )
    },
    {
      key: 'bookmarked',
      label: (
        <Badge count={bookmarkedSearches?.length || 0}>
          Bookmarked Searches
        </Badge>
      ),
      children: (
        <div className={cssStyles.searchHistoryTabpaneContent}>
          {renderSearches(bookmarkedSearches, 'bookmarked')}
        </div>
      )
    },
    {
      key: 'archived',
      label: (
        <Badge count={archivedSearches?.length || 0}>
          Archived Searches
        </Badge>
      ),
      children: (
        <div className={cssStyles.searchHistoryTabpaneContent}>
          {renderSearches(archivedSearches, 'archived')}
        </div>
      )
    }
  ];

  const renderEditModal = () => (
    <Modal
      title="Edit Search"
      open={editModalVisible}
      onCancel={() => setEditModalVisible(false)}
      onOk={handleEditSubmit}
      okText="Save Changes"
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          title: '',
          location: '',
          jobType: '',
          experience: '',
          keyword: '',
          minSalary: '',
          maxSalary: '',
          radius: '30'
        }}
      >
        <Form.Item
          name="title"
          label="Search Title"
          rules={[{ required: true, message: 'Please enter a search title' }]}
        >
          <Input placeholder="Enter a title for this search" />
        </Form.Item>
        
        <Form.Item
          name="keyword"
          label="Keyword or Job Title"
          rules={[{ required: true, message: 'Please enter a keyword or job title' }]}
        >
          <Input placeholder="Enter keywords or job title" />
        </Form.Item>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="location"
              label="Location"
            >
              <LocationSelect 
                placeholder="City, state, or zip code"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="radius"
              label="Radius (miles)"
            >
              <Select>
                <Option value="10">10 miles</Option>
                <Option value="20">20 miles</Option>
                <Option value="30">30 miles</Option>
                <Option value="50">50 miles</Option>
                <Option value="100">100 miles</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="jobType"
              label="Job Type"
            >
              <Select placeholder="Select job type">
                <Option value="fulltime">Full Time</Option>
                <Option value="parttime">Part Time</Option>
                <Option value="contract">Contract</Option>
                <Option value="temporary">Temporary</Option>
                <Option value="internship">Internship</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="experience"
              label="Experience Level"
            >
              <Select placeholder="Select experience level">
                <Option value="entry">Entry Level</Option>
                <Option value="mid">Mid Level</Option>
                <Option value="senior">Senior Level</Option>
                <Option value="executive">Executive</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="minSalary"
              label="Min Salary (k)"
            >
              <Input type="number" placeholder="Min salary" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="maxSalary"
              label="Max Salary (k)"
            >
              <Input type="number" placeholder="Max salary" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );

  return (
    <div className={cssStyles.searchHistoryContainer}>
      <div className={cssStyles.searchHistoryHeader}>
        <SearchControls
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          sortBy={sortBy}
          onSortChange={setSortBy}
          dateFilter={dateFilter}
          onDateFilterChange={setDateFilter}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
      </div>
      <div className={cssStyles.searchHistoryContent}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className={cssStyles.searchHistoryTabs}
          items={tabItems}
        />
      </div>
      {renderEditModal()}
    </div>
  );
};

export default React.memo(SearchHistory);

